import { Form, Input } from "antd-v5";
import classNames from "classnames";
import React, {
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { FindingSummary } from "types/api/findingSummary";
import { extractCVSSVersion } from "utils/cvssUtil";

import ACLink from "ac-components/ACLink/ACLink";
import ACButton from "ac-components/ac-button/ACButton";
import ACModalV2 from "ac-components/ac-modal/ACModalV2";
import ACSegmented from "ac-components/ac-segmented/ACSegmented";

import CVSS3_1VersionFields from "./CVSS3_1VersionFields";
import StandardVersionFields from "./StandardVersionFields";

const NewCVSSCalculator = ({
  initialCvssVector = "",
  selectedCVE,
  onSubmit: onSubmitFromProps,
  viewMode,
  editableModifiersFor3v1,
  finding,
}: {
  initialCvssVector: string;
  selectedCVE: string | undefined;
  onSubmit?: (params: {
    vectorString: string;
    baseScore?: number;
    note?: string;
  }) => Promise<void> | void;
  viewMode?: boolean;
  editableModifiersFor3v1?: boolean;
  finding?: FindingSummary;
}) => {
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(
    () => extractCVSSVersion(initialCvssVector) || "None",
  );
  const [finalVector, setFinalVector] = useState(initialCvssVector);
  const [form] = Form.useForm();
  const [finalScore, setFinalScore] = useState<number | null | undefined>(null);
  const [isComplete, setIsComplete] = useState(false);
  const modalSettings = ACModalV2.useModalSettings();
  const [noteHasChanged, setNoteHasChanged] = useState(false);
  const initialVersion = useMemo(
    () => extractCVSSVersion(initialCvssVector) || "None",
    [initialCvssVector],
  );

  const metricsHaveChanged =
    version !== initialVersion || finalVector !== initialCvssVector;

  const hasChanges = metricsHaveChanged || noteHasChanged;

  useEffect(() => {
    modalSettings.updateShowWarningOnClose(hasChanges, {
      promptMessage: "Unsaved Changes, are you sure you want to proceed?",
      okText: "Yes, Don't Save Changes",
      cancelText: "Back",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges]);

  const [resetKey, setResetKey] = useState(0);
  const isEditable = !viewMode || editableModifiersFor3v1;

  const handleUpdate = useCallback(
    (data: { vector: string; score: number | null | undefined }) => {
      setFinalVector(data.vector);
      setFinalScore(data.score);
    },
    [],
  );

  const handleCompletenessChange = useCallback((complete: boolean) => {
    setIsComplete(complete);
  }, []);

  const onVersionChange = (value: string) => {
    setVersion(value);
    // Clear final data when switching tabs
    setFinalVector("");
    setFinalScore(null);
    setIsComplete(false);
  };

  const handleReset = () => {
    const initialVersion = extractCVSSVersion(initialCvssVector) || "None";
    setVersion(initialVersion);
    setResetKey((prevKey) => prevKey + 1);
  };

  const onSubmit = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      if (onSubmitFromProps) {
        await onSubmitFromProps({
          baseScore: finalScore || undefined,
          vectorString: finalVector,
          note: form.getFieldValue("note"),
        });
      }
      modalSettings.updateClosedState({
        cvssVector: finalVector,
        cvssScore: finalScore,
      });
      setNoteHasChanged(false);
      modalSettings.updateShowWarningOnClose(false);
      modalSettings.close();
    } catch (e) {
      setLoading(false);
    }
  };

  const onNoteChange = () => {
    setNoteHasChanged(true);
  };

  return (
    <>
      <ACModalV2.Title>CVSS Calculator</ACModalV2.Title>
      <div className="cvss-metrics-wrapper p-b-0">
        <div className="d-flex align-items-center justify-content-between">
          <ACSegmented
            items={[
              { label: "CVSS 2.0", value: "2.0" },
              { label: "CVSS 3.0", value: "3.0" },
              { label: "CVSS 3.1", value: "3.1" },
              { label: "CVSS 4.0", value: "4.0" },
              "None",
            ]}
            onChange={
              onVersionChange as FormEventHandler<HTMLDivElement> &
                ((value: string) => void)
            }
            value={version}
            //disable Version tab switch for view mode
            disabled={viewMode}
          />
          <div>
            <ACLink
              primary={false}
              overlay
              href={`/compliance/vulnerability-rules/${selectedCVE}/redirect?findingId=${finding?.id ? finding.id : ""}`}
              target="_blank"
            >
              {selectedCVE}
            </ACLink>
          </div>
        </div>
      </div>
      {version === "3.1" ? (
        <CVSS3_1VersionFields
          resetKey={resetKey}
          initialCvssVector={initialCvssVector}
          onUpdate={handleUpdate}
          onCompletenessChange={handleCompletenessChange}
          editableModifiersFor3v1={editableModifiersFor3v1}
          viewMode={viewMode}
          subProductID={finding?.subProduct?.id}
          environmentName={finding?.envName}
        />
      ) : version !== "None" ? (
        <StandardVersionFields
          resetKey={resetKey}
          version={version}
          initialCvssVector={initialCvssVector}
          onUpdate={handleUpdate}
          onCompletenessChange={handleCompletenessChange}
          form={form}
          viewMode={viewMode}
        />
      ) : (
        <div className="m-b-m p-m">
          {" "}
          Click Save to reset the value of CVSS Vector and Base Score{" "}
        </div>
      )}
      {version !== "None" && isEditable && (
        <div className="cvss-metrics-wrapper">
          <Form
            form={form}
            layout="vertical"
            disabled={loading}
            onValuesChange={onNoteChange}
          >
            <Form.Item
              name="note"
              label="Note"
              rules={[
                { required: true, message: "Note is required" },
                {
                  max: 1000,
                  message: "Maximum length is 1000 characters",
                },
              ]}
            >
              <Input.TextArea />
            </Form.Item>
          </Form>
        </div>
      )}
      <ACModalV2.Footer
        className={classNames("d-flex", {
          "justify-content-end-imp": !isEditable,
        })}
      >
        {!isEditable ? (
          <ACButton
            onClick={() => modalSettings.close()}
            type="ghost"
            disabled={loading}
          >
            Cancel
          </ACButton>
        ) : (
          <>
            <ACButton onClick={handleReset} type="ghost" disabled={loading}>
              Clear
            </ACButton>
            <ACButton
              onClick={onSubmit}
              disabled={(!isComplete && version !== "None") || !hasChanges}
              loading={loading}
            >
              Save
            </ACButton>
          </>
        )}
      </ACModalV2.Footer>
    </>
  );
};
export default NewCVSSCalculator;
